import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { setApiBearerToken } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authVersionRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const authVersion = authVersionRef.current;
    api.get('/auth/me', { skipAuthExpiredEvent: true })
      .then(res => { if (!cancelled && authVersionRef.current === authVersion) setUser(res.data); })
      .catch(() => { if (!cancelled && authVersionRef.current === authVersion) setUser(null); })
      .finally(() => {
        if (!cancelled && authVersionRef.current === authVersion) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('kitchen-auth-expired', handleUnauthorized);
    return () => window.removeEventListener('kitchen-auth-expired', handleUnauthorized);
  }, []);

  const login = useCallback(async (pin) => {
    authVersionRef.current += 1;
    const { data } = await api.post('/auth/login', { pin });
    setApiBearerToken(data.token);
    setUser(data);
    setLoading(false);
    return data;
  }, []);

  const logout = useCallback(async () => {
    authVersionRef.current += 1;
    try {
      await api.post('/auth/logout');
    } finally {
      setApiBearerToken('');
      setUser(null);
    }
  }, []);

  const authContextValue = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

